import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import {
  ExpiryCountdown,
  PayAddressCard,
  RequestStatusBadge,
  asPaymentStatus,
  useDepositRequest,
  type PaymentStatus,
} from "@/entities/wallet";
import { ApiError } from "@/shared/api";
import { ROUTES } from "@/shared/config/routes";
import { formatDateTime, formatMoney } from "@/shared/lib";
import { Button, RollingNumber } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Заявка на пополнение: адрес, ход платежа и исход.
 *
 * Раньше экран был про ручной ввод хеша и ожидание оператора. С переходом на
 * NOWPayments он стал экраном ПЛАТЕЖА: адрес выдаётся шлюзом отдельно на каждую
 * заявку, состояние приходит колбэком, баланс зачисляется сам.
 *
 * Ветка выбирается по `payment.payment_status` — слову шлюза, — а не по
 * `status` заявки: заявка стоит в `pending` и пока деньги в пути, и когда
 * пришла недоплата, а для человека это совершенно разные новости.
 *
 * **`payment === null` — законное состояние, а не сбой.** Так выглядят заявки,
 * созданные до перехода на шлюз: они ещё живут в проде, и их по-прежнему
 * закрывает оператор. Экран обязан их отрисовать, а не упасть.
 */

/** Показывать ли адрес. */
function showsAddress(status: PaymentStatus): boolean {
  // Только пока перевода ещё ждут. Как только он найден в блокчейне, адрес
  // убирается: человек, увидевший его на экране «подтверждается», отправляет
  // второй раз — и это уже не пополнение, а повторный депозит, который
  // зачисляется вручную.
  return status === "waiting";
}

export function DepositStatusPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const depositId = Number(id);

  const { data, isPending, isError, error, refetch } = useDepositRequest(depositId);

  if (!Number.isFinite(depositId)) {
    return (
      <ScreenState tone="info" icon="questionMark" title={t("wallet.notFound.title")} body={t("wallet.notFound.body")} />
    );
  }

  if (isPending) return <ScreenSkeleton blocks={[120, 140, 80]} />;

  if (isError) {
    const notFound = error instanceof ApiError && error.code === "NOT_FOUND";
    return (
      <ScreenState
        tone={notFound ? "info" : "loss"}
        icon={notFound ? "questionMark" : "warningFill"}
        title={t(notFound ? "wallet.notFound.title" : "states.error.title")}
        body={t(notFound ? "wallet.notFound.body" : "states.error.body")}
        {...(notFound ? {} : { action: { label: t("states.error.action"), onClick: () => void refetch() } })}
      />
    );
  }

  const payment = data.payment ?? null;
  const status = payment ? asPaymentStatus(payment.payment_status) : null;

  const hint = (() => {
    if (!payment) return t("wallet.payment.legacyHint");
    switch (status) {
      case "waiting":
        return t("wallet.payment.waitingHint");
      case "confirming":
      case "confirmed":
      case "sending":
        return t("wallet.payment.confirmingHint");
      case "finished":
        return t("wallet.payment.finishedHint");
      case "partially_paid":
        return t("wallet.payment.partialHint");
      case "expired":
        return t("wallet.payment.expiredHint");
      default:
        return t("wallet.payment.failedHint");
    }
  })();

  const canRetry = status === "expired" || status === "failed" || data.status === "rejected";

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("wallet.requestTitle")} subtitle={formatDateTime(data.created_at)} />

      <section className="flex flex-col gap-3 rounded-3xl border border-border-subtle bg-background-elevated p-5">
        <div className="flex items-start justify-between">
          <RollingNumber
            value={formatMoney(data.amount, { suffix: data.asset ?? "USDT" })}
            className="text-h2 font-bold text-text-primary"
          />
          <RequestStatusBadge status={data.status} />
        </div>

        {data.network ? (
          <span className="text-caption font-normal text-text-secondary">
            {t("wallet.network")}: {data.network}
          </span>
        ) : null}

        {status ? (
          <span className="text-caption font-medium text-text-primary">
            {t(`wallet.payment.status.${status}`)}
          </span>
        ) : null}

        <p className="text-caption font-normal text-text-secondary">{hint}</p>

        {/* Недоплата: цифры важнее слов — человек должен увидеть, сколько
            именно не дошло, чтобы понять, о чём с ним свяжется оператор. */}
        {payment && status === "partially_paid" ? (
          <span className="text-caption font-normal text-status-warning">
            {t("wallet.payment.received", {
              amount: payment.actually_paid,
              currency: payment.pay_currency.toUpperCase(),
            })}
          </span>
        ) : null}

        {data.admin_comment ? (
          <p className="text-caption font-normal text-text-secondary">{data.admin_comment}</p>
        ) : null}

        {payment?.expires_at && status === "waiting" ? (
          <ExpiryCountdown
            expiresAt={payment.expires_at}
            // По нулю не решаем локально, а спрашиваем сервер: срок принадлежит ему.
            onExpire={() => void refetch()}
          />
        ) : null}
      </section>

      {payment && showsAddress(status ?? "waiting") ? (
        <PayAddressCard payment={payment} />
      ) : null}

      {/* Адрес заявки, созданной до перехода на шлюз: показываем как было. */}
      {!payment && data.address ? (
        <section className="flex flex-col gap-2 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          <span className="text-caption font-medium text-text-secondary">{t("wallet.address")}</span>
          <span className="min-w-0 font-mono text-caption break-all text-text-primary">
            {data.address}
          </span>
        </section>
      ) : null}

      {data.tx_hash ? (
        <section className="flex flex-col gap-2 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          <span className="text-caption font-medium text-text-secondary">{t("wallet.txHash")}</span>
          <span className="font-mono text-caption break-all text-text-primary">{data.tx_hash}</span>
        </section>
      ) : null}

      {canRetry ? (
        <Button size="cta" block onClick={() => void navigate(ROUTES.depositAsset)}>
          {t("wallet.payment.newRequest")}
        </Button>
      ) : null}
    </div>
  );
}
